import { apiService } from '../../../components/api/apiService';
import { logDataModels } from './dataModelsLogger';

const CONNECTION = 'backend_py';

export type PolicyTestStep = {
  id: string;
  title: string;
  detail: string;
};

export type PolicyTestResult = {
  allow: boolean;
  applies: boolean;
  denyReason: string;
  originalQuery: string;
  rewrittenQuery: string;
  applied: { rowFilters: number; columnMasks: string[] };
  bindings: Record<string, string>;
  steps: PolicyTestStep[];
  catalog: string;
  columns: Array<{ name: string; type: string }>;
  rows: Record<string, unknown>[];
  rowCount: number;
  truncated: boolean;
};

function hideEngineName(text: string): string {
  return (text || '')
    .replace(/\btrino\s+preview\b/gi, 'live preview')
    .replace(/\btrino\s+/gi, '')
    .replace(/\btrino\b/gi, '')
    .replace(/\bnetwork error\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function serviceErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as {
    response?: { data?: { error?: { message?: string }; message?: string; detail?: unknown } };
    message?: string;
  };
  const detail = axiosErr.response?.data?.detail;
  const detailText = typeof detail === 'string' ? detail : '';
  const cleaned = hideEngineName(
    axiosErr.response?.data?.error?.message
    || axiosErr.response?.data?.message
    || detailText
    || axiosErr.message
    || '',
  );
  return cleaned || fallback;
}

export async function runPolicyTest(input: {
  policyId: string;
  assetId: string;
  query: string;
  testIdentity: {
    userId: string;
    userName?: string;
    userEmail?: string;
    roles: string[];
    groups: string[];
    attributes: Record<string, string>;
    variables: Record<string, string>;
  };
}): Promise<PolicyTestResult> {
  logDataModels('app', 'governance_policy_test_requested', {
    policyId: input.policyId,
    assetId: input.assetId,
    userId: input.testIdentity.userId,
  });
  try {
    const data = (await apiService.post(
      '/api/v1/trino/policy-test',
      {
        policyId: input.policyId,
        assetId: input.assetId,
        query: input.query,
        testIdentity: input.testIdentity,
      },
      CONNECTION,
    )) as Record<string, unknown>;
    const applied = data.applied && typeof data.applied === 'object' ? data.applied as Record<string, unknown> : {};
    const steps = Array.isArray(data.steps)
      ? data.steps.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object').map((item) => ({
        id: String(item.id || ''),
        title: hideEngineName(String(item.title || '')),
        detail: hideEngineName(String(item.detail || '')),
      }))
      : [];
    const columns = Array.isArray(data.columns)
      ? data.columns.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object').map((item) => ({
        name: String(item.name || ''),
        type: String(item.type || 'unknown'),
      })).filter((item) => item.name)
      : [];
    const rows = Array.isArray(data.rows)
      ? data.rows.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
      : [];
    const result: PolicyTestResult = {
      allow: Boolean(data.allow),
      applies: data.applies !== false,
      denyReason: hideEngineName(String(data.denyReason ?? data.deny_reason ?? '')),
      originalQuery: String(data.originalQuery ?? data.original_query ?? input.query),
      rewrittenQuery: String(data.rewrittenQuery ?? data.rewritten_query ?? ''),
      applied: {
        rowFilters: Number(applied.rowFilters ?? applied.row_filters ?? 0) || 0,
        columnMasks: Array.isArray(applied.columnMasks)
          ? applied.columnMasks.map((item) => String(item))
          : Array.isArray(applied.column_masks)
            ? applied.column_masks.map((item) => String(item))
            : [],
      },
      bindings: data.bindings && typeof data.bindings === 'object'
        ? Object.fromEntries(Object.entries(data.bindings as Record<string, unknown>).map(([key, value]) => [key, String(value)]))
        : {},
      steps,
      catalog: String(data.catalog || ''),
      columns,
      rows,
      rowCount: Number(data.rowCount ?? data.row_count ?? rows.length) || 0,
      truncated: Boolean(data.truncated),
    };
    logDataModels(result.allow ? 'app' : 'warning', 'governance_policy_test_ok', {
      policyId: input.policyId,
      allow: result.allow,
      rows: result.rowCount,
      steps: result.steps.length,
    });
    return result;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to run policy test');
    logDataModels('error', 'governance_policy_test_failed', { policyId: input.policyId, message });
    throw new Error(message);
  }
}
